import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  KanbanSquare,
  Loader2,
  Play,
  Plus,
  RotateCcw,
  Send,
  MessageSquare,
} from 'lucide-react'

import {
  approveCollaborationAssignment,
  approveProductionAssignment,
  listCollaborationTaskAssignments,
  listCollaborationTasks,
  listMembers,
  listProductionAssignments,
  getProduction,
  publishCollaborationTask,
  requestProductionAssignmentChanges,
  startProductionAssignment,
  submitProductionAssignment,
  type CollaborationTask,
  type CollaborationTaskAssignment,
  type ProductionAssignment,
  type ProductionMember,
  type Production,
} from '@/api/studio-production'
import {
  PRODUCTION_INSET_PANEL_CLASS,
  PRODUCTION_TABLE_HEAD_CLASS,
  PRODUCTION_TABLE_ROW_CLASS,
  PRODUCTION_TABLE_ROW_HOVER_CLASS,
} from '@/features/studio/lib/production-shell'
import { cn } from '@/shared/lib/utils'
import { CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { PageHeading } from '@/shared/components/PageHeading'
import { ProductionAssignmentsTable } from '@/features/studio/components/ProductionAssignmentsTable'
import { useAuth } from '@/hooks'
import { useClientPagination } from '@/features/studio/hooks/useClientPagination'
import { BountyTaskCreateModal } from '@/features/studio/modals/BountyTaskCreateModal'
import {
  ProtoBadge,
  ProtoBtn,
  ProtoListTable,
  ProtoPagination,
  ProtoTabs,
} from '@/features/studio/components/ui-kit'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { useAssignmentItemLabels } from '@/features/studio/hooks/useAssignmentItemLabels'

const PAGE_SIZE = 20

function assignmentHasShotScope(a: ProductionAssignment): boolean {
  return (a.items ?? []).some(
    (it) => it.item_type === 'shot' || it.item_type === 'shot_scene',
  )
}

const STATUS_TONE: Record<string, 'amber' | 'emerald' | 'indigo' | 'rose' | 'default'> = {
  pending: 'amber',
  in_progress: 'indigo',
  submitted: 'rose',
  approved: 'emerald',
  changes_requested: 'amber',
  draft: 'default',
  published: 'indigo',
  closed: 'default',
}

export function TasksPage() {
  const { productionId = '' } = useParams()
  const { t } = useTranslation()
  const base = productionBasePath(productionId)
  const { user } = useAuth()
  const [tab, setTab] = useState<'internal' | 'bounty'>('internal')
  const [assignments, setAssignments] = useState<ProductionAssignment[]>([])
  const [members, setMembers] = useState<ProductionMember[]>([])
  const [production, setProduction] = useState<Production | null>(null)
  const [bountyTasks, setBountyTasks] = useState<CollaborationTask[]>([])
  const [assignmentsByTask, setAssignmentsByTask] = useState<Record<number, CollaborationTaskAssignment[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [bountyCreateOpen, setBountyCreateOpen] = useState(false)
  const [expandedTaskId, setExpandedTaskId] = useState<number | null>(null)
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({})

  const { resolve: resolveScopeLabel, loading: labelsLoading } = useAssignmentItemLabels(productionId)

  const reloadInternal = useCallback(async () => {
    const [assignRes, memRes, prod] = await Promise.all([
      listProductionAssignments(productionId, undefined, { limit: 500, offset: 0 }),
      listMembers(productionId, { limit: 500, offset: 0 }).catch(() => ({ items: [] as ProductionMember[] })),
      getProduction(productionId).catch(() => null),
    ])
    setAssignments(assignRes.items)
    setMembers(memRes.items)
    setProduction(prod)
  }, [productionId])

  const reloadBounty = useCallback(async () => {
    const res = await listCollaborationTasks(productionId)
    setBountyTasks(res.items)
  }, [productionId])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await Promise.all([reloadInternal(), reloadBounty()])
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'tasks.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [reloadInternal, reloadBounty])

  useEffect(() => {
    void reload()
  }, [reload])

  const {
    page: internalPage,
    setPage: setInternalPage,
    total: internalTotal,
    items: internalPageItems,
  } = useClientPagination(assignments, PAGE_SIZE)

  const {
    page: bountyPage,
    setPage: setBountyPage,
    total: bountyTotal,
    items: bountyPageItems,
  } = useClientPagination(bountyTasks, PAGE_SIZE)

  useEffect(() => {
    setInternalPage(0)
    setBountyPage(0)
  }, [tab, setInternalPage, setBountyPage])

  async function runAssignmentAction(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await reloadInternal()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'tasks.updateFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function publishBounty(task: CollaborationTask) {
    setBusy(true)
    try {
      await publishCollaborationTask(productionId, task.id)
      await reloadBounty()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'tasks.bountyPublishFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function loadAssignments(taskId: number) {
    if (expandedTaskId === taskId) {
      setExpandedTaskId(null)
      return
    }
    setExpandedTaskId(taskId)
    try {
      const res = await listCollaborationTaskAssignments(productionId, taskId)
      setAssignmentsByTask((prev) => ({ ...prev, [taskId]: res.items }))
    } catch {
      setAssignmentsByTask((prev) => ({ ...prev, [taskId]: [] }))
    }
  }

  async function approveAssignment(taskId: number, assignment: CollaborationTaskAssignment) {
    setBusy(true)
    try {
      await approveCollaborationAssignment(productionId, taskId, assignment.id)
      const res = await listCollaborationTaskAssignments(productionId, taskId)
      setAssignmentsByTask((prev) => ({ ...prev, [taskId]: res.items }))
      await reloadBounty()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'tasks.bountyApproveFailed'))
    } finally {
      setBusy(false)
    }
  }

  function renderAssignmentActions(a: ProductionAssignment) {
    const uid = user?.id ?? 0
    const isAssignee = a.assignee_id === uid
    const reviewNote = reviewNotes[a.public_id] ?? ''
    const isOwner =
      !!production?.is_owner ||
      (production?.user_id != null && uid > 0 && production.user_id === uid)
    const myMember = members.find((m) => m.user_id === uid)
    const canReview = isOwner || !!myMember?.is_department_admin

    return (
      <div className="flex flex-wrap justify-end gap-1">
        {isAssignee && (a.status === 'pending' || a.status === 'changes_requested') ? (
          <ProtoBtn
            size="sm"
            disabled={busy}
            onClick={() =>
              void runAssignmentAction(() => startProductionAssignment(productionId, a.public_id))
            }
          >
            <Play className="h-3.5 w-3.5" />
            {t('tasks.startWork')}
          </ProtoBtn>
        ) : null}
        {isAssignee && a.status === 'in_progress' ? (
          <ProtoBtn
            size="sm"
            disabled={busy}
            onClick={() =>
              void runAssignmentAction(() => submitProductionAssignment(productionId, a.public_id))
            }
          >
            <Send className="h-3.5 w-3.5" />
            {assignmentHasShotScope(a) ? t('tasks.submitReview') : t('tasks.submitAcceptance')}
          </ProtoBtn>
        ) : null}
        {a.status === 'submitted' && canReview ? (
          assignmentHasShotScope(a) ? (
            <>
              <Link
                to={`${base}/reviews`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-200 transition hover:bg-indigo-500/20"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {t('tasks.openReviewCenter')}
              </Link>
              <ProtoBtn
                size="sm"
                variant="soft"
                tone="emerald"
                disabled={busy}
                onClick={() =>
                  void runAssignmentAction(() =>
                    approveProductionAssignment(productionId, a.public_id, {
                      note: reviewNote.trim() || undefined,
                    }),
                  )
                }
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t('tasks.approveAssignment')}
              </ProtoBtn>
              <ProtoBtn
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  void runAssignmentAction(() =>
                    requestProductionAssignmentChanges(productionId, a.public_id, {
                      note: reviewNote.trim() || undefined,
                    }),
                  )
                }
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('tasks.requestChanges')}
              </ProtoBtn>
            </>
          ) : (
            <>
              <ProtoBtn
                size="sm"
                disabled={busy}
                onClick={() =>
                  void runAssignmentAction(() =>
                    approveProductionAssignment(productionId, a.public_id, {
                      note: reviewNote.trim() || undefined,
                    }),
                  )
                }
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t('tasks.approve')}
              </ProtoBtn>
              <ProtoBtn
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  void runAssignmentAction(() =>
                    requestProductionAssignmentChanges(productionId, a.public_id, {
                      note: reviewNote.trim() || undefined,
                    }),
                  )
                }
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('tasks.requestChanges')}
              </ProtoBtn>
            </>
          )
        ) : null}
      </div>
    )
  }

  const tableLoading = loading || (tab === 'internal' && labelsLoading)

  return (
    <CatalogListPageShell>
      <PageHeading icon={KanbanSquare} tone="indigo" title={t('tasks.boardTitle')} subtitle={t('tasks.boardSubtitle')}>
        {tab === 'bounty' ? (
          <CatalogListPrimaryButton onClick={() => setBountyCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('tasks.bountyCreate')}
          </CatalogListPrimaryButton>
        ) : null}
      </PageHeading>

      <div className="mb-6">
        <ProtoTabs
          production
          tabs={[
            { id: 'internal', label: t('tasks.tabInternal') },
            { id: 'bounty', label: t('tasks.tabBounty') },
          ]}
          active={tab}
          onChange={(id) => setTab(id as 'internal' | 'bounty')}
        />
      </div>

      {error ? <p className="mb-4 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}

      {tableLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
        </div>
      ) : tab === 'internal' ? (
        <>
          <ProductionAssignmentsTable
            assignments={internalPageItems}
            members={members}
            emptyMessage={t('tasks.assignmentEmpty')}
            resolveScopeLabel={resolveScopeLabel}
            showWorkflowColumns
            reviewNotes={reviewNotes}
            onReviewNoteChange={(publicId, note) =>
              setReviewNotes((prev) => ({ ...prev, [publicId]: note }))
            }
            renderActions={renderAssignmentActions}
          />
          {internalTotal > 0 ? (
            <ProtoPagination
              page={internalPage}
              pageSize={PAGE_SIZE}
              total={internalTotal}
              onPageChange={setInternalPage}
              prevLabel={t('common.prevPage')}
              nextLabel={t('common.nextPage')}
            />
          ) : null}
        </>
      ) : (
        <>
          <ProtoListTable production>
            <table className="min-w-full text-left text-sm">
              <thead className={cn(PRODUCTION_TABLE_HEAD_CLASS, 'text-xs text-zinc-500')}>
                <tr>
                  <th className="px-4 py-3">{t('tasks.colTitle')}</th>
                  <th className="px-4 py-3">{t('tasks.colDescription')}</th>
                  <th className="px-4 py-3">{t('team.assignmentsColStatus')}</th>
                  <th className="px-4 py-3">{t('tasks.colReward')}</th>
                  <th className="px-4 py-3">{t('tasks.colSlots')}</th>
                  <th className="px-4 py-3 text-right">{t('team.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {bountyPageItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-xs text-zinc-500">
                      {t('tasks.bountyEmpty')}
                    </td>
                  </tr>
                ) : (
                  bountyPageItems.map((task) => {
                    const expanded = expandedTaskId === task.id
                    const taskAssignments = assignmentsByTask[task.id] ?? []
                    return (
                      <Fragment key={task.id}>
                        <tr className={cn(PRODUCTION_TABLE_ROW_HOVER_CLASS, 'align-top')}>
                          <td className="px-4 py-3 font-medium text-zinc-100">{task.title}</td>
                          <td className="max-w-[240px] px-4 py-3 text-xs text-zinc-500">
                            {task.description?.trim() || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <ProtoBadge tone={STATUS_TONE[task.status] ?? 'default'}>{task.status}</ProtoBadge>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-amber-200/80">
                            {t('tasks.bountyRewardPts', { n: task.reward_points })}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-zinc-400">
                            {t('tasks.bountySlots', { taken: task.slots_taken, total: task.slots_total })}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap justify-end gap-1">
                              {task.status === 'draft' ? (
                                <ProtoBtn size="sm" disabled={busy} onClick={() => void publishBounty(task)}>
                                  {t('tasks.bountyPublish')}
                                </ProtoBtn>
                              ) : null}
                              <ProtoBtn size="sm" variant="ghost" onClick={() => void loadAssignments(task.id)}>
                                {expanded ? (
                                  <ChevronDown className="h-3.5 w-3.5" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5" />
                                )}
                                {t('tasks.bountyAssignments')}
                              </ProtoBtn>
                            </div>
                          </td>
                        </tr>
                        {expanded ? (
                          <tr className={cn(PRODUCTION_TABLE_ROW_CLASS, 'bg-[#0e0e16]/60')}>
                            <td colSpan={6} className="px-4 py-3">
                              {taskAssignments.length === 0 ? (
                                <p className="text-xs text-zinc-500">{t('tasks.bountyNoAssignments')}</p>
                              ) : (
                                <ul className="space-y-2">
                                  {taskAssignments.map((a) => (
                                    <li
                                      key={a.id}
                                      className={cn(PRODUCTION_INSET_PANEL_CLASS, 'flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs')}
                                    >
                                      <span className="text-zinc-400">
                                        #{a.user_id} · {a.status}
                                        {a.deliverable_note ? ` · ${a.deliverable_note}` : ''}
                                      </span>
                                      {a.status === 'submitted' ? (
                                        <ProtoBtn
                                          size="sm"
                                          disabled={busy}
                                          onClick={() => void approveAssignment(task.id, a)}
                                        >
                                          <CheckCircle2 className="h-3.5 w-3.5" />
                                          {t('tasks.bountyApprove')}
                                        </ProtoBtn>
                                      ) : null}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </ProtoListTable>
          <ProtoPagination
            page={bountyPage}
            pageSize={PAGE_SIZE}
            total={bountyTotal}
            onPageChange={setBountyPage}
            prevLabel={t('common.prevPage')}
            nextLabel={t('common.nextPage')}
          />
        </>
      )}

      <BountyTaskCreateModal
        open={bountyCreateOpen}
        onClose={() => setBountyCreateOpen(false)}
        productionId={productionId}
        onCreated={() => void reloadBounty()}
      />
    </CatalogListPageShell>
  )
}
